import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import { fetchProducts } from "@/lib/api";
import { MediaTile } from "@/components/MediaTile";
import { Sparkles } from "lucide-react";

const SESSION_KEY = "vini_offer_popup_shown";

/**
 * Shows a one-time-per-session popup announcing the latest active Offer or
 * Festival item, with a "See Offer" button that takes the visitor to the
 * dedicated New Arrivals / Offers page for full details.
 */
export const OfferPopup = () => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [offer, setOffer] = useState(null);

  useEffect(() => {
    // Only show once per browser session, and never on the offers page itself.
    if (sessionStorage.getItem(SESSION_KEY)) return;

    (async () => {
      try {
        const [offers, festival] = await Promise.all([
          fetchProducts("offers"),
          fetchProducts("festival"),
        ]);
        const candidates = [...offers, ...festival]
          .filter((p) => p.is_active)
          .sort((a, b) => (a.display_order ?? 0) - (b.display_order ?? 0));

        if (candidates.length > 0) {
          setOffer(candidates[0]);
          setOpen(true);
        }
      } catch {
        // Silently skip the popup if products can't be fetched — never block the site.
      }
    })();
  }, []);

  const handleClose = (val) => {
    setOpen(val);
    if (!val) sessionStorage.setItem(SESSION_KEY, "1");
  };

  const handleSeeOffer = () => {
    sessionStorage.setItem(SESSION_KEY, "1");
    setOpen(false);
    navigate("/new-arrivals");
  };

  if (!offer) return null;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-sm bg-card border-primary/30 p-0 overflow-hidden" data-testid="offer-popup">
        {offer.media_url ? (
          <div className="relative h-48 w-full bg-muted">
            <MediaTile
              src={offer.media_url}
              mediaType={offer.media_type}
              alt={offer.name}
              className="absolute inset-0 w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-card via-card/20 to-transparent" />
          </div>
        ) : (
          <div className="h-24 w-full flex items-center justify-center bg-primary/10">
            <Sparkles className="h-10 w-10 text-primary" />
          </div>
        )}

        <div className="p-6 pt-4 text-center">
          <span className="section-eyebrow justify-center">
            <span className="h-px w-6 bg-primary/60" />
            Special Offer
            <span className="h-px w-6 bg-primary/60" />
          </span>
          <DialogTitle className="font-heading text-2xl font-semibold text-foreground mt-3">
            {offer.name}
          </DialogTitle>
          <DialogDescription className="mt-2 text-sm text-muted-foreground">
            {offer.status_text || offer.description || "A special offer is live right now — tap below for full details."}
          </DialogDescription>

          <div className="mt-6 flex flex-col gap-3">
            <Button variant="premium" size="lg" onClick={handleSeeOffer} data-testid="offer-popup-see-offer-btn">
              See Offer
            </Button>
            <button
              onClick={() => handleClose(false)}
              className="text-xs text-muted-foreground hover:text-foreground transition-colors"
              data-testid="offer-popup-dismiss-btn"
            >
              Maybe later
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
